import { Component, Inject, PLATFORM_ID } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ArticleInfoComponent } from './article-info/article-info.component';
import { ArticleSectionsComponent } from './article-sections/article-sections.component';
import { ArticleSeoComponent } from './article-seo/article-seo.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TabsModule } from 'primeng/tabs';
import { ArticleStickyLinkComponent } from './article-sticky-link/article-sticky-link.component';
import {
  Article,
  ArticleService,
  ArticleType,
  Section,
  PictureData,
} from '../../../shared/public-api';

@Component({
  selector: 'admin-article',
  templateUrl: './article.component.html',
  styleUrls: ['./article.component.scss'],
  imports: [
    CommonModule,
    ButtonModule,
    ArticleInfoComponent,
    ArticleSectionsComponent,
    ArticleSeoComponent,
    ProgressSpinnerModule,
    ToastModule,
    ConfirmDialogModule,
    TabsModule,
    RouterModule,
    ArticleStickyLinkComponent,
  ],
  providers: [MessageService],
})
export class ArticleComponent {
  articleModel: Article;
  loading: boolean;

  linkOptions = {
    decorators: {
      isExternal: {
        mode: 'manual',
        label: 'newTab',
        defaultValue: true,
        attributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
      isDownloadable: {
        mode: 'manual',
        defaultValue: false,
        label: 'Downloadable',
        attributes: {
          download: '',
        },
      },
    },
  };

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private articleService: ArticleService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.articleModel = new Article();
    this.articleModel.draft = true;
    this.articleModel.hasSummary = false;
    this.articleModel.sections = [];
    this.articleModel.stickyLinks = [];
    this.articleModel.articleType = ArticleType.Article;
    if (route.snapshot.params && route.snapshot.params['articleId']) {
      this.loadArticle(route.snapshot.params['articleId']);
    } else {
      if (route.snapshot.queryParams && route.snapshot.queryParams['articleType']) {
        this.articleModel.articleType = +route.snapshot.queryParams['articleType'];
      }
    }
  }

  loadArticle(articleId: string): void {
    this.loading = true;
    this.articleService
      .getArticle(articleId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((article) => {
        article.sections = article.sections?.sort((a, b) => a.order - b.order);
        this.articleModel = article;
        if (!this.articleModel.stickyLinks) {
          this.articleModel.stickyLinks = [];
        }
      });
  }

  save(): void {
    this.fixOrders();
    if (!this.articleModel.id) {
      return this.createArticle();
    }
    return this.updateArticle();
  }

  createArticle(): void {
    this.loading = true;
    let emptyPictureSection = false;
    this.articleModel.sections
      .filter((x) => x.componentName === 'picture')
      .forEach((s) => {
        emptyPictureSection = !this.checkEmptyPictureSection(s);
      });
    if (emptyPictureSection) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Il y a des section de type image avec des colonnes vide!',
      });
      this.loading = false;
      return;
    }
    this.articleService
      .createArticle(this.articleModel)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((res) => {
        if (res === null) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Une erreur est survenue!',
          });
        } else {
          this.messageService.add({
            severity: 'success',
            summary: 'Ok',
            detail: 'Article sauvé!',
          });
          this.router.navigate([res.id], { relativeTo: this.route });
        }
      });
  }

  updateArticle(): void {
    this.loading = true;
    let emptyPictureSection = false;
    this.articleModel.sections
      .filter((x) => x.componentName === 'picture')
      .forEach((s) => {
        emptyPictureSection = !this.checkEmptyPictureSection(s);
      });
    if (emptyPictureSection) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Il y a des section de type image avec des colonnes vide!',
      });
      this.loading = false;
      return;
    }
    this.articleService
      .updateArticle(this.articleModel)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((res) => {
        if (res === null) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Une erreur est survenue!',
          });
        } else {
          this.messageService.add({
            severity: 'success',
            summary: 'Ok',
            detail: 'Article sauvé!',
          });
          this.loadArticle(this.articleModel.id);
        }
      });
  }

  private checkEmptyPictureSection(section: Section<PictureData>): boolean {
    if (!section) {
      return false;
    }
    const columns = section.sectionData.pictureColumns;
    let res = true;
    if (columns.length === 0) {
      res = false;
      return res;
    }
    columns.forEach((c) => {
      if (c.pictures.length === 0) {
        res = false;
        return;
      }
    });
    return res;
  }

  private fixOrders(): void {
    this.articleModel.sections.forEach((s, i) => {
      s.order = i;
    });
  }
}
